import React, { useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Home from './pages/Home';
import About from './pages/About';
import Scholarships from './pages/Scholarships';
import Donate from './pages/Donate';
import DonationResult from './pages/DonationResult';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import EventDetail from './pages/EventDetail';
import News from './pages/News';
import NewsDetail from './pages/NewsDetail';
import Contact from './pages/Contact';
import Volunteer from './pages/Volunteer';
import AdminLogin from './pages/AdminLogin';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboardV2 from './pages/admin/AdminDashboardV2';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import AdminLogs from './pages/admin/AdminLogs';
import AdminSettings from './pages/admin/AdminSettings';
import AdminHero from './pages/admin/AdminHero';
import AdminNews from './pages/admin/AdminNews';
import AdminUploads from './pages/admin/AdminUploads';
import { AdminPresidents, AdminBoard, AdminEvents, AdminPress, AdminBankAccounts, AdminSponsors, AdminPublications, AdminDonationTypes, AdminSpecialDays, AdminKvkkDocs, AdminTaxDocs, AdminFAQ, AdminVolunteerBireysel, AdminVolunteerKurumsal, AdminMilestones } from './pages/admin/ContentPages';
import AdminProjects from './pages/admin/AdminProjectsTabs';
import { AdminDonations, AdminScholarships, AdminVolunteers, AdminMessages } from './pages/admin/AdminSubmissions';
import AdminUsers from './pages/admin/AdminUsers';
import AdminPageTexts from './pages/admin/AdminPageTexts';
import AdminHomepage from './pages/admin/AdminHomepage';
import AdminStaticPages from './pages/admin/AdminStaticPages';
import AdminBackup from './pages/admin/AdminBackup';
import AdminSEO from './pages/admin/AdminSEO';
import AdminEmailTemplates from './pages/admin/AdminEmailTemplates';
import AdminVisibility from './pages/admin/AdminVisibility';
import AdminMenu from './pages/admin/AdminMenu';
import AdminPages from './pages/admin/AdminPages';
import CustomPage from './pages/CustomPage';
import PageGate from './components/PageGate';
import AdminEditBar from './components/AdminEditBar';
import DonationTypes from './pages/DonationTypes';
import SpecialDay from './pages/SpecialDay';
import DonationChannels from './pages/DonationChannels';
import Presidents from './pages/Presidents';
import Boards from './pages/Boards';
import Sponsors from './pages/Sponsors';
import MediaMentions from './pages/MediaMentions';
import Publications from './pages/Publications';
import School from './pages/School';
import EducationUnits from './pages/EducationUnits';
import TaxExemption from './pages/TaxExemption';
import KVKK from './pages/KVKK';
import Header from './components/Header';
import Footer from './components/Footer';
import { Toaster } from './components/ui/sonner';
import VersionWatcher from './components/VersionWatcher';
import { useLocation } from 'react-router-dom';
import { api } from './lib/api';

function getOrCreateSessionId() {
  let sid = localStorage.getItem('yekuv_sid');
  if (!sid) {
    sid = (crypto?.randomUUID?.() || (Date.now() + Math.random().toString(36))).replace(/-/g, '').slice(0, 24);
    localStorage.setItem('yekuv_sid', sid);
  }
  return sid;
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.substring(1));
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });

    // Track pageview (skip admin pages)
    if (!pathname.startsWith('/admin')) {
      api.trackPageview({
        path: pathname + (hash || ''),
        referrer: document.referrer || '',
        title: document.title || '',
        session_id: getOrCreateSessionId(),
      });
    }
  }, [pathname, hash]);
  return null;
}

function Layout({ children }) {
  return (
    <>
      <AdminEditBar />
      <Header />
      <main>{children}</main>
      <Footer />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <VersionWatcher />
        <Routes>
          <Route path="/" element={<Layout><PageGate slug="home"><Home /></PageGate></Layout>} />
          <Route path="/kurumsal" element={<Layout><PageGate slug="about"><About /></PageGate></Layout>} />
          <Route path="/kurumsal/baskanlar" element={<Layout><PageGate slug="presidents"><Presidents /></PageGate></Layout>} />
          <Route path="/kurumsal/kurullar" element={<Layout><PageGate slug="boards"><Boards /></PageGate></Layout>} />
          <Route path="/kurumsal/medya" element={<Layout><PageGate slug="media"><MediaMentions /></PageGate></Layout>} />
          <Route path="/kurumsal/yayinlar" element={<Layout><PageGate slug="publications"><Publications /></PageGate></Layout>} />
          {/* Legacy Turkish-char URLs — redirect to correct route with type filter */}
          <Route path="/Yayınlarımız" element={<Navigate to="/kurumsal/yayinlar" replace />} />
          <Route path="/yayinlarimiz" element={<Navigate to="/kurumsal/yayinlar" replace />} />
          <Route path="/Dergilerimiz" element={<Navigate to="/kurumsal/yayinlar?type=dergi" replace />} />
          <Route path="/dergilerimiz" element={<Navigate to="/kurumsal/yayinlar?type=dergi" replace />} />
          <Route path="/dergiler" element={<Navigate to="/kurumsal/yayinlar?type=dergi" replace />} />
          <Route path="/kurumsal/okul" element={<Layout><PageGate slug="school"><School /></PageGate></Layout>} />
          <Route path="/kurumsal/birimler" element={<Layout><PageGate slug="units"><EducationUnits /></PageGate></Layout>} />
          <Route path="/kurumsal/destekciler" element={<Layout><PageGate slug="sponsors"><Sponsors /></PageGate></Layout>} />
          <Route path="/kurumsal/vergi" element={<Layout><PageGate slug="tax"><TaxExemption /></PageGate></Layout>} />
          <Route path="/kurumsal/kvkk" element={<Layout><PageGate slug="kvkk"><KVKK /></PageGate></Layout>} />
          <Route path="/burslar" element={<Layout><PageGate slug="scholarships"><Scholarships /></PageGate></Layout>} />
          <Route path="/bagis" element={<Layout><PageGate slug="donate"><Donate /></PageGate></Layout>} />
          <Route path="/bagis/sonuc" element={<Layout><DonationResult /></Layout>} />
          <Route path="/bagis/turler" element={<Layout><PageGate slug="donation_types"><DonationTypes /></PageGate></Layout>} />
          <Route path="/bagis/ozel-gun" element={<Layout><PageGate slug="special_day"><SpecialDay /></PageGate></Layout>} />
          <Route path="/bagis/kanallar" element={<Layout><PageGate slug="donation_channels"><DonationChannels /></PageGate></Layout>} />
          <Route path="/projeler" element={<Layout><PageGate slug="projects"><Projects /></PageGate></Layout>} />
          <Route path="/projeler/:slug" element={<Layout><PageGate slug="projects"><ProjectDetail /></PageGate></Layout>} />
          <Route path="/etkinlikler/:slug" element={<Layout><PageGate slug="projects"><EventDetail /></PageGate></Layout>} />
          <Route path="/duyurular" element={<Layout><PageGate slug="news"><News /></PageGate></Layout>} />
          <Route path="/duyurular/:slug" element={<Layout><PageGate slug="news"><NewsDetail /></PageGate></Layout>} />
          <Route path="/iletisim" element={<Layout><PageGate slug="contact"><Contact /></PageGate></Layout>} />
          <Route path="/gonullu" element={<Layout><PageGate slug="volunteer"><Volunteer /></PageGate></Layout>} />
          <Route path="/sayfa/:slug" element={<Layout><CustomPage /></Layout>} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboardV2 />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="logs" element={<AdminLogs />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="hero" element={<AdminHero />} />
            <Route path="news" element={<AdminNews />} />
            <Route path="presidents" element={<AdminPresidents />} />
            <Route path="board" element={<AdminBoard />} />
            <Route path="projects" element={<AdminProjects />} />
            <Route path="events" element={<AdminEvents />} />
            <Route path="press" element={<AdminPress />} />
            <Route path="sponsors" element={<AdminSponsors />} />
            <Route path="publications" element={<AdminPublications />} />
            <Route path="page-texts" element={<AdminPageTexts />} />
            <Route path="homepage" element={<AdminHomepage />} />
            <Route path="static-pages" element={<AdminStaticPages />} />
            <Route path="backup" element={<AdminBackup />} />
            <Route path="seo" element={<AdminSEO />} />
            <Route path="email-templates" element={<AdminEmailTemplates />} />
            <Route path="visibility" element={<AdminVisibility />} />
            <Route path="menu" element={<AdminMenu />} />
            <Route path="pages" element={<AdminPages />} />
            <Route path="bank-accounts" element={<AdminBankAccounts />} />
            <Route path="donation-types" element={<AdminDonationTypes />} />
            <Route path="special-days" element={<AdminSpecialDays />} />
            <Route path="kvkk-docs" element={<AdminKvkkDocs />} />
            <Route path="tax-docs" element={<AdminTaxDocs />} />
            <Route path="faq" element={<AdminFAQ />} />
            <Route path="volunteer-form-bireysel" element={<AdminVolunteerBireysel />} />
            <Route path="volunteer-form-kurumsal" element={<AdminVolunteerKurumsal />} />
            <Route path="milestones" element={<AdminMilestones />} />
            <Route path="uploads" element={<AdminUploads />} />
            <Route path="donations" element={<AdminDonations />} />
            <Route path="scholarships" element={<AdminScholarships />} />
            <Route path="volunteers" element={<AdminVolunteers />} />
            <Route path="messages" element={<AdminMessages />} />
            <Route path="users" element={<AdminUsers />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </div>
  );
}

export default App;
